import { Id } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define the shape of the billing details data (adjust based on actual API response)
interface CompanyBillingDetails {
  billing_currency: string;
  billing_hourly_rate: number;
  billing_per_transaction_rate: string; // Changed to string
  // Add other relevant fields if needed
}

export interface CompanyDetailsState {
  details: CompanyBillingDetails | null;
  isLoading: boolean;
  error?: AppError | null;
}

// Payload type definitions (following crud.ts patterns)
export interface FetchStartPayload {
  companyId: Id;
}

export interface FetchSuccessPayload {
  details: CompanyBillingDetails;
}

export interface FetchFailurePayload {
  error?: AppError;
}

// Action type definitions
export type FetchStartAction = PayloadAction<FetchStartPayload>;
export type FetchSuccessAction = PayloadAction<FetchSuccessPayload>;
export type FetchFailureAction = PayloadAction<FetchFailurePayload>;

const name = "state/features/companyDetails";

export const initialState: CompanyDetailsState = {
  details: null,
  isLoading: false,
  error: null,
};

const companyDetailsSlice = createSlice({
  name,
  initialState,
  reducers: {
    fetchStart: {
      reducer: (state: CompanyDetailsState, _action: FetchStartAction) => {
        state.isLoading = true;
        state.error = null;
      },
      prepare: (companyId: Id) => ({
        payload: { companyId }
      })
    },
    fetchSuccess: {
      reducer: (state: CompanyDetailsState, action: FetchSuccessAction) => {
        state.isLoading = false;
        state.details = action.payload.details;
      },
      prepare: (details: CompanyBillingDetails) => ({
        payload: { details }
      })
    },
    fetchFailure: {
      reducer: (state: CompanyDetailsState, action: FetchFailureAction) => {
        state.isLoading = false;
        state.error = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
  },
});

export const {
  fetchStart: fetchCompanyBillingDetailsStart,
  fetchSuccess: fetchCompanyBillingDetailsSuccess,
  fetchFailure: fetchCompanyBillingDetailsFailure,
} = companyDetailsSlice.actions;

// Selectors
const selectCompanyDetailsState = (state: {
  features: { CompanyDetails: CompanyDetailsState }; // Ensure this matches the key in the root reducer
}): CompanyDetailsState => state.features.CompanyDetails;

export const selectCompanyBillingDetails = createSelector(
  selectCompanyDetailsState,
  (state) => state.details
);

export const selectIsCompanyDetailsLoading = createSelector(
  selectCompanyDetailsState,
  (state) => state.isLoading
);

export const selectCompanyDetailsError = createSelector(
  selectCompanyDetailsState,
  (state) => state.error
);

export default companyDetailsSlice.reducer; 